import { DateTimeString, requireContext, toast, uuidv4 } from 'common';
import { Accessor, createContext, createEffect, createMemo, createSignal, on, Setter, Signal } from 'solid-js';
import { createStore, SetStoreFunction, Store } from 'solid-js/store';
import { Conversation, Message, MessageRole, Trace, supportAssistAgent as api, UserRating, ConversationOverview, StreamingEnvelope } from 'st-app/api';
import { showConfirmation } from '../st-confirmation-dialog/st-confirmation-dialog';

export const TICKET_VIEW_ID = 'C30E8A12-CAFD-4A7B-8302-B9F3C54B355C'

const createEmptyConversation = (): Conversation => ({
    conversationGU: uuidv4().toUpperCase(),
    messages: [],
    title: undefined,
    timestamp: new Date().toISOString() as DateTimeString,
    isArchived: false,
    ticketGU: undefined,
})

export class SuppportAssistAgentContext {
    conversations: ConversationOverview[]; setConversations: SetStoreFunction<ConversationOverview[]>
    conversationsLoading: Accessor<boolean>; setConversationsLoading: Setter<boolean>;

    conversation: Store<Conversation>; setConversation: SetStoreFunction<Conversation>;
    conversationLoading: Accessor<boolean>; setConversationLoading: Setter<boolean>;

    referencesVisible: Accessor<boolean>; setReferencesVisible: Setter<boolean>;
    message: Accessor<string>; setMessage: Setter<string>;

    editingConversationGU: Accessor<string | null>; setEditingConversationGU: Setter<string | null>;

    ticketGU: Accessor<string | undefined>; setTicketGU: Setter<string | undefined>;

    constructor(ticketGU?: string) {
        [this.ticketGU, this.setTicketGU] = createSignal<string | undefined>(ticketGU);
        [this.conversations, this.setConversations] = createStore<ConversationOverview[]>([]);
        [this.conversationsLoading, this.setConversationsLoading] = createSignal<boolean>(false);
        [this.conversationLoading, this.setConversationLoading] = createSignal<boolean>(false);
        [this.conversation, this.setConversation] = createStore<Conversation>(createEmptyConversation());
        [this.message, this.setMessage] = createSignal<string>('');
        [this.referencesVisible, this.setReferencesVisible] = createSignal(true);
        [this.editingConversationGU, this.setEditingConversationGU] = createSignal<string | null>(null);
    }

    startNewConversation() {
        this.setConversation(createEmptyConversation());
    }

    private buildMessage(conversationGU: string, role: MessageRole, text: string): Message {
        return {
            messageGU: uuidv4().toUpperCase(),
            conversationGU,
            timestamp: new Date().toISOString() as DateTimeString,
            role,
            text,
            rating: UserRating.None,
            traces: [],
        }
    }

    async sendMessage(message: string, isAnalysis: boolean = false) {
        const conversationGU = this.conversation.conversationGU;
        if (!conversationGU) {
            toast.notify('error', 'Failed to send message: conversationGU is missing');
            return;
        }

        const timestamp = new Date().toISOString() as DateTimeString;

        const userMessage: Message = this.buildMessage(conversationGU, MessageRole.User, message);
        const assistantMessage: Message = this.buildMessage(conversationGU, MessageRole.Assistant, '');
        this.setMessage('');

        this.setConversation('messages', (messages) => [...messages, userMessage, assistantMessage]);

        // If no overview exists for this conversation, create one (first message)
        const isFirstMessage = !this.conversations.some(c => c.conversationGU === conversationGU);
        if (isFirstMessage) {
            this.setConversations([{ conversationGU, timestamp, title: undefined, isArchived: false, ticketGU: this.ticketGU() }, ...this.conversations]);
        } else {
            // Update timestamp so the conversation bubbles to the correct time group
            this.setConversations(c => c.conversationGU === conversationGU, 'timestamp', timestamp as DateTimeString);
        }

        try {
            for await (const envelope of api.PromptAgent(message, conversationGU, this.ticketGU() || '')) {
                try {
                    const event = parseEnvelope(envelope);
                    this.receiveEvent(event, conversationGU, assistantMessage.messageGU!);
                } catch (e) {
                    console.error('Error processing chunk:', e);
                }
            }
        } catch (error) {
            console.error('Error in streamSupportAssist:', error);
            toast.notify('error', 'Failed to stream support assist response');
            this.setConversation('messages', (messages) => messages.slice(0, -1));
        }

        if (isFirstMessage) {
            try {
                const title = await api.CreateInitialTitle(conversationGU, message, this.ticketGU() || '', isAnalysis);
                if (title) {
                    this.setConversations(c => c.conversationGU === conversationGU, 'title', title);
                    if (this.conversation.conversationGU === conversationGU)
                        this.setConversation('title', title);
                }
            } catch (e) {
                console.error('Failed to generate initial title:', e);
            }
        }
    }

    receiveEvent(event: StreamingEvent, conversationGu: string, assistantMessageGu: string) {
        switch (event.type) {
            case 'conversation.created':
                this.setConversations(c => c.conversationGU === conversationGu, 'title', event.title);
                break;
            case 'text.delta':
                this.setConversation('messages', m => m.messageGU === assistantMessageGu, 'text', m => (m || '') + event.delta);
                break;
            case 'text.done':
                this.setConversation('messages', m => m.messageGU === assistantMessageGu, 'text', event.text);
                break;
            case 'agent.rationale':
            case 'agent.document_reference':
            case 'agent.ticket_reference':
                const trace: Trace = {
                    messageGU: assistantMessageGu,
                    eventTime: new Date().toISOString() as DateTimeString,
                    type: event.type,
                    json: JSON.stringify(event),
                }

                this.setConversation('messages', m => m.messageGU === assistantMessageGu, 'traces', traces => [...(traces || []), trace]);
                break;
        }
    }

    async loadConversations() {
        this.setConversationsLoading(true);
        try {
            const conversations = await api.LoadConversations(this.ticketGU() || '');
            this.setConversations(conversations);
        } catch (error) {
            console.error('Error loading conversations:', error);
            toast.notify('error', 'Failed to load conversations');
        }
        this.setConversationsLoading(false);
    }

    async loadConversation(id: string) {
        this.setConversationLoading(true);
        try {
            const conversation = await api.LoadConversation(id);
            const messages = conversation.messages.toSorted((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            this.setConversation(conversation);
            this.setConversation('messages', messages);
        } catch (error) {
            toast.notify('error', 'Failed to load conversation');
            console.error('Error loading conversation:', error);
        }
        this.setConversationLoading(false);
    }

    async deleteConversation(id: string) {
        try {
            await showConfirmation('Delete Conversation', 'Are you sure you want to delete this conversation? This action cannot be undone.');
        } catch (error) {
            return;
        }

        this.setConversations(this.conversations.filter(c => c.conversationGU !== id));
        
        await api.DeleteConversation(id);

        if (this.conversation.conversationGU === id) {
            this.startNewConversation();
        }

    }

    async renameConversation(id: string, title: string) {
        this.setConversations(c => c.conversationGU === id, 'title', title);
        if (this.conversation.conversationGU === id) {
            this.setConversation('title', title);
        }
        await api.RenameConversation(id, title);
    }

    async sendStarterPrompt() {
        console.log('Sending starter prompt for ticketGU:', this.ticketGU());
        const prompt = await api.GetStarterPrompt(this.ticketGU() || '', this.message());
        if (prompt) {
            await this.sendMessage(prompt, true);
        }
    }

    async shareConversation(id: string) {
        throw new Error('Not implemented yet'); // TODO: Implement share conversation
    }

    async likeMessage(message: Message) {
        this.setConversation('messages', m => m.messageGU === message.messageGU, 'rating', UserRating.Positive);
        await api.LikeMessage(message.messageGU!);
    }

    async dislikeMessage(message: Message) {
        this.setConversation('messages', m => m.messageGU === message.messageGU, 'rating', UserRating.Negative);
        await api.DislikeMessage(message.messageGU!);
    }
}


export const SuppportAssistAgentContextJsx = createContext<SuppportAssistAgentContext>();
export const requireSuppportAssistAgentContext = () => {
    return requireContext(SuppportAssistAgentContextJsx);
}


export const parseTrace = (trace: Trace): StreamingEvent => {
    const type = trace.type as StreamingEventType;
    let data: any = {};
    try {
        data = trace.json ? JSON.parse(trace.json) : {};
    } catch { }
    return { type, ...data };
}

const parseEnvelope = (envelope: StreamingEnvelope): StreamingEvent => {
    const type = envelope.type as StreamingEventType;
    const data = envelope.data ? JSON.parse(envelope.data) : {};
    return { type, ...data };
}

export type StreamingEventType = StreamingEvent['type'];

export interface AgentDocumentReferenceEvent {
    type: 'agent.document_reference';
    filename: string;
    content: string;
}

export interface AgentRationaleEvent {
    type: 'agent.rationale';
    text: string;
}

export interface AgentTicketReferenceEvent {
    type: 'agent.ticket_reference';
    ticketNumber: string;
    title: string;
    content: string;
}

export interface ConversationCreatedEvent {
    type: 'conversation.created';
    conversationGU: string;
    title: string;
}

export interface ResponseCompletedEvent {
    type: 'response.completed';
    success: boolean;
    errorMessage?: string;
    content?: string;
    rawData?: string;
    responseTimeMS?: number;
    creditsRemaining?: number;
}

export interface ResponseFailedEvent {
    type: 'response.failed';
    code: string;
    message: string;
}

export interface TextDeltaEvent {
    type: 'text.delta';
    delta: string;
}

export interface TextDoneEvent {
    type: 'text.done';
    text: string;
}

export type StreamingEvent = AgentDocumentReferenceEvent
                            | AgentRationaleEvent
                            | AgentTicketReferenceEvent
                            | ConversationCreatedEvent
                            | ResponseCompletedEvent
                            | ResponseFailedEvent
                            | TextDeltaEvent
                            | TextDoneEvent;